import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { lazy, Suspense, type ReactNode } from "react";
import { AuthProvider, useAuth } from "./lib/AuthContext";
import { ConfigProvider } from "./lib/ConfigContext";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Library from "./pages/Library";
import { Brand } from "./components/Brand";

// Lazy-loaded — each gets its own chunk so the initial payload stays lean.
// Reader drags in react-pdf + pdfjs-dist (~400 KB). Wizard has a large
// component tree. Atelier + BookDetail are also route-scoped.
// v2.81 — Studio is the user-facing name for the creation surface (renamed
// from "The Wizard" 2026-05-27). Component file stays at pages/Wizard.tsx —
// internal name kept stable to avoid a noise rename; user-facing strings +
// route both say Studio. /new and /wizard 301 to /studio.
const Studio      = lazy(() => import("./pages/Wizard"));
// v2.81 — Atelier (the conversational chat surface) consolidated into the
// Studio. All /atelier* routes now redirect to /studio. Code archived at
// _archive/atelier_v1/. AdminAtelier (read-only ops view) is unaffected.
const BookDetail  = lazy(() => import("./pages/BookDetail"));
const BookLanding = lazy(() => import("./pages/BookLanding"));
const BookBuilding = lazy(() => import("./pages/BookBuilding"));
const BuildById   = lazy(() => import("./pages/BuildById"));
const Reader        = lazy(() => import("./pages/Reader"));
const ChapterReader = lazy(() => import("./pages/ChapterReader"));
const ChapterReview = lazy(() => import("./pages/ChapterReview"));
const DreamBook   = lazy(() => import("./pages/DreamBook"));
const DBEngine    = lazy(() => import("./pages/DBEngine"));
const Workshop    = lazy(() => import("./pages/Workshop"));
const Marketing   = lazy(() => import("./pages/Marketing"));
const MyBooks     = lazy(() => import("./pages/MyBooks"));
const Pricing     = lazy(() => import("./pages/Pricing"));
const Account     = lazy(() => import("./pages/Account"));
const AuthorPage  = lazy(() => import("./pages/AuthorPage"));
const DeconstructedReader = lazy(() => import("./pages/DeconstructedReader"));
const NavMockups = lazy(() => import("./pages/NavMockups"));
// Throwaway — Studio right-panel design exploration. URL-only, not in nav.
const StudioMocks = lazy(() => import("./pages/StudioMocks"));

// Admin SPA — lifecycle pipeline + operator console (v1.58 re-platform).
// Mounted at /admin/* during the gradual migration; legacy
// server-rendered pages stay at /admin/* for now.
const AdminLayout    = lazy(() => import("./admin/AdminLayout").then((m) => ({ default: m.AdminLayout })));
const AdminDashboard = lazy(() => import("./admin/pages/Dashboard").then((m) => ({ default: m.Dashboard })));
const AdminOperations = lazy(() => import("./admin/pages/Operations").then((m) => ({ default: m.Operations })));
const AdminApprovals = lazy(() => import("./admin/pages/Approvals").then((m) => ({ default: m.Approvals })));
const AdminBooks     = lazy(() => import("./admin/pages/Books").then((m) => ({ default: m.Books })));
const AdminBookDetail = lazy(() => import("./admin/pages/BookDetail").then((m) => ({ default: m.BookDetail })));
const AdminJobs      = lazy(() => import("./admin/pages/Jobs").then((m) => ({ default: m.Jobs })));
const AdminEngines   = lazy(() => import("./admin/pages/Engines").then((m) => ({ default: m.Engines })));
const AdminEngineDetail = lazy(() => import("./admin/pages/EngineDetail").then((m) => ({ default: m.EngineDetail })));
const AdminProposalDetail = lazy(() => import("./admin/pages/ProposalDetail").then((m) => ({ default: m.ProposalDetail })));
// v2.14 — full React parity for the 13 remaining admin nav items
// (Content / People / System groups). Each page is a thin React shell
// over a new /api/admin/<name> JSON endpoint that mirrors what the
// legacy /admin-2/<name> HTML page surfaced. Mutation-heavy surfaces
// (Users tier/suspend, Admins invite/role, Settings edit, Incidents
// create) deep-link to the legacy form via LegacyLink for now; the
// listing + read-only views are full React.
const AdminProposals = lazy(() => import("./admin/pages/Proposals").then((m) => ({ default: m.Proposals })));
const AdminLibrary   = lazy(() => import("./admin/pages/Library").then((m) => ({ default: m.Library })));
const AdminBPB       = lazy(() => import("./admin/pages/BPB").then((m) => ({ default: m.BPB })));
const AdminAtelier   = lazy(() => import("./admin/pages/Atelier").then((m) => ({ default: m.Atelier })));
const AdminUsers     = lazy(() => import("./admin/pages/Users").then((m) => ({ default: m.Users })));
const AdminAdmins    = lazy(() => import("./admin/pages/Admins").then((m) => ({ default: m.Admins })));
const AdminComms     = lazy(() => import("./admin/pages/Comms").then((m) => ({ default: m.Comms })));
const AdminAudit     = lazy(() => import("./admin/pages/Audit").then((m) => ({ default: m.Audit })));
const AdminIncidents = lazy(() => import("./admin/pages/Incidents").then((m) => ({ default: m.Incidents })));
const AdminModeration= lazy(() => import("./admin/pages/Moderation").then((m) => ({ default: m.Moderation })));
const AdminBilling   = lazy(() => import("./admin/pages/Billing").then((m) => ({ default: m.Billing })));
const AdminSettings  = lazy(() => import("./admin/pages/Settings").then((m) => ({ default: m.Settings })));
const AdminInsights  = lazy(() => import("./admin/pages/Insights").then((m) => ({ default: m.Insights })));


function Loading() {
  return (
    <div className="min-h-screen grid place-items-center">
      <div className="text-center">
        <Brand />
        <p className="mt-4 text-xs text-ink-300 smallcaps">Opening the observatory…</p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, ready } = useAuth();
  const loc = useLocation();
  if (!ready) return <Loading />;
  if (!user) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  return <>{children}</>;
}

function GuestOnlyRoute({ children }: { children: ReactNode }) {
  const { user, ready } = useAuth();
  if (!ready) return <Loading />;
  if (user) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <ConfigProvider>
      <AuthProvider>
        <Suspense fallback={<Loading />}>
          <Routes>
          <Route path="/login" element={<GuestOnlyRoute><Login /></GuestOnlyRoute>} />
          <Route path="/register" element={<GuestOnlyRoute><Register /></GuestOnlyRoute>} />
          {/* Public surfaces — published books are readable by anyone. The
              page components themselves show CTAs to sign in or register
              when a logged-out visitor tries to use author-only features. */}
          <Route path="/library" element={<Library />} />
          <Route path="/author/:userId" element={<AuthorPage />} />
          <Route path="/books/:id" element={<BookLanding />} />
          <Route path="/books/:id/legacy" element={<BookDetail />} />
          <Route path="/books/:id/building" element={<ProtectedRoute><BookBuilding /></ProtectedRoute>} />
          <Route path="/builds/:build_id" element={<ProtectedRoute><BuildById /></ProtectedRoute>} />
          <Route path="/books/:id/review" element={<ProtectedRoute><ChapterReview /></ProtectedRoute>} />
          <Route path="/books/:id/read" element={<Reader />} />
          <Route path="/books/:id/chapter/:n" element={<ChapterReader />} />
          <Route path="/books/:id/blocks/:n" element={<DeconstructedReader />} />
          {/* Creation chooser — visible to everyone; the Studio it links to
              gates behind auth. Keeps /dreambook shareable with beta
              users who haven't signed up yet. */}
          <Route path="/dreambook" element={<DreamBook />} />
          {/* v2.93 — Writers Workshop: Characters, Plots, Voices. Author-only. */}
          <Route path="/workshop" element={<ProtectedRoute><Workshop /></ProtectedRoute>} />
          <Route path="/db-engine" element={<DBEngine />} />
          <Route path="/pricing" element={<Pricing />} />
          {/* Author-only surfaces */}
          <Route path="/my-books" element={<ProtectedRoute><MyBooks /></ProtectedRoute>} />
          <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
          {/* Internal — visual decision surfaces. No auth needed; mockup-only.
              Lives at /nav-mockups (not /admin/*) so it bypasses the
              gate without needing a separate nginx allowlist entry. */}
          <Route path="/nav-mockups" element={<NavMockups />} />
          <Route path="/studio-mocks" element={<StudioMocks />} />
          {/* Admin SPA — operator console at /admin/*. v1.67 swapped
              with the legacy server-rendered admin (now /admin-2/*).
              AdminLayout itself probes the admin session cookie and
              bounces to /admin/login (still proxied to FastAPI by
              nginx) if missing. */}
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="today"     element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="operations" element={<AdminOperations />} />
            {/* N3 — legacy views the Operations cockpit replaced (M9) */}
            <Route path="pipeline"  element={<Navigate to="/admin/operations" replace />} />
            <Route path="approvals" element={<AdminApprovals />} />
            <Route path="books"     element={<AdminBooks />} />
            <Route path="books/:id" element={<AdminBookDetail />} />
            <Route path="runs"      element={<Navigate to="/admin/operations" replace />} />
            <Route path="jobs"      element={<AdminJobs />} />
            <Route path="engines"   element={<AdminEngines />} />
            <Route path="engines/:code" element={<AdminEngineDetail />} />
            <Route path="proposals"      element={<AdminProposals />} />
            <Route path="proposals/:id"  element={<AdminProposalDetail />} />
            <Route path="library"        element={<AdminLibrary />} />
            <Route path="bpb"            element={<AdminBPB />} />
            <Route path="atelier"        element={<AdminAtelier />} />
            <Route path="users"          element={<AdminUsers />} />
            <Route path="admins"         element={<AdminAdmins />} />
            <Route path="comms"          element={<AdminComms />} />
            <Route path="audit"          element={<AdminAudit />} />
            <Route path="incidents"      element={<AdminIncidents />} />
            <Route path="moderation"     element={<AdminModeration />} />
            <Route path="billing"        element={<AdminBilling />} />
            <Route path="settings"       element={<AdminSettings />} />
            <Route path="insights"       element={<AdminInsights />} />
          </Route>
          {/* Legacy /admin-react/* paths redirect to the new /admin/*
              so any open tabs / bookmarks survive the URL swap. */}
          <Route path="/admin-react" element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="/admin-react/*" element={<Navigate to="/admin/dashboard" replace />} />
          {/* /studio is the canonical creation surface (renamed from /new
              and /wizard 2026-05-27). One surface, both fresh-start and
              upload-first manuscript paths flow through here. */}
          <Route path="/studio" element={<ProtectedRoute><Studio /></ProtectedRoute>} />
          {/* Legacy redirects — bookmarks survive the rename. */}
          <Route path="/new" element={<Navigate to="/studio" replace />} />
          <Route path="/wizard" element={<Navigate to="/studio" replace />} />
          {/* Atelier consolidated into Studio 2026-05-27 — code archived at
              _archive/atelier_v1/; route paths kept as redirects so any
              live tabs / bookmarks survive. */}
          <Route path="/atelier" element={<Navigate to="/studio" replace />} />
          <Route path="/atelier/:id" element={<Navigate to="/studio" replace />} />
          <Route path="/atelier-old/:id" element={<Navigate to="/studio" replace />} />
          <Route path="/atelier-v2/:id" element={<Navigate to="/studio" replace />} />
          {/* Marketing landing — same content for logged-in and logged-out,
              so the page is shareable with beta testers without surprises. */}
          <Route path="/" element={<Marketing />} />
          <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </AuthProvider>
    </ConfigProvider>
  );
}
